import { useEffect, useMemo, useState } from "react";
import {
  alpha,
  Avatar,
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { useAtom } from "jotai";
import { useSearchParams } from "react-router-dom";
import {
  useHasPermission,
  UserPermission,
} from "@zdistancelab-packages/ui-permission-validator";
import { Button, DataGrid, Modal } from "../../common";
import {
  selectedOrgIdAtom,
  userAtom,
  userPermissionAtom,
} from "../../../atoms";
import { useFetchOrganizationsQuery } from "../../../api";
import { OrganizationModel, UserModel } from "../../../models";
import {
  CREW_ALL_FILTER,
  CREW_MAX_WEEKLY_HOURS,
  CREW_ROLE_CHIP_COLORS,
  CREW_ROLES_WITHOUT_CAN_DRIVE,
  CREW_ROLE_LABELS,
  CREW_STATUS_CHIP_COLORS,
  CREW_STATUS_FILTERS,
  CREW_VISIBLE_SKILLS,
} from "../../../constants";
import { Action, Module, Role } from "../../../enums";
import { CrewMember, DropdownItem, User } from "../../../types";
import { formatCurrency, theme } from "../../../utils";
import {
  CrewAvailability,
  CrewFilterChips,
  CrewForm,
  CrewSkillTag,
  CrewSummaryCards,
} from "./fragments";

const getRoleLabel = (member: CrewMember) =>
  CREW_ROLE_LABELS[member.roleCode] ?? member.roleName;

const EmptyCell = () => (
  <Typography sx={{ fontSize: 13, color: "var(--dashboard-text-secondary)" }}>
    —
  </Typography>
);

const getFullName = (member: CrewMember) =>
  `${member.firstName} ${member.lastName}`;

const getInitials = (member: CrewMember) =>
  `${member.firstName[0] ?? ""}${member.lastName[0] ?? ""}`.toUpperCase();

type CrewMembersProps = {
  users: User[];
  isLoading: boolean;
  onCreateMember: (member: CrewMember) => void;
  onUpdateMember: (member: CrewMember) => void;
  onOrgChange: (orgId: number) => void;
};

export const CrewMembers = ({
  users,
  isLoading,
  onCreateMember,
  onUpdateMember,
  onOrgChange,
}: CrewMembersProps) => {
  const [user] = useAtom(userAtom);
  const [queryParams] = useSearchParams();
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);
  const [userPermission] = useAtom<UserPermission>(userPermissionAtom);
  const crew = useMemo(() => UserModel.buildCrewMembers(users), [users]);
  const [roleFilter, setRoleFilter] = useState(CREW_ALL_FILTER);
  const [statusFilter, setStatusFilter] = useState(CREW_ALL_FILTER);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [selectedMember, setSelectedMember] = useState<CrewMember>();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [organizationsDropdown, setOrganizationsDropdown] = useState<
    DropdownItem[]
  >([]);
  const isSuperAdmin = user?.roleCode === Role.SuperAdmin;
  const hasWritePermission = useHasPermission(userPermission, [
    { module: Module.User, action: Action.Write },
  ]);
  const fetchOrganizationsQuery = useFetchOrganizationsQuery({
    enabled: isSuperAdmin,
  });

  useEffect(() => {
    if (fetchOrganizationsQuery.isSuccess) {
      setOrganizationsDropdown(
        OrganizationModel.buildOrganizationDropdown(
          fetchOrganizationsQuery.data,
        ),
      );
    } else if (fetchOrganizationsQuery.isError) {
      setOrganizationsDropdown([]);
    }
  }, [
    fetchOrganizationsQuery.isSuccess,
    fetchOrganizationsQuery.isError,
    fetchOrganizationsQuery.data,
  ]);

  useEffect(() => {
    if (organizationsDropdown.length > 0 && !selectedOrgId) {
      onOrgChange(
        Number(queryParams.get("orgId")) ||
          user?.orgId ||
          (organizationsDropdown[0].value as number),
      );
    }
  }, [organizationsDropdown, selectedOrgId, onOrgChange]);

  const roleFilters = useMemo(() => {
    const roles = new Map(
      crew.map((member) => [member.roleCode, getRoleLabel(member)]),
    );

    return [
      { label: CREW_ALL_FILTER, value: CREW_ALL_FILTER },
      ...Array.from(roles, ([value, label]) => ({ label, value })),
    ];
  }, [crew]);

  const filteredCrew = useMemo(
    () =>
      crew.filter(
        (member) =>
          (roleFilter === CREW_ALL_FILTER || member.roleCode === roleFilter) &&
          (statusFilter === CREW_ALL_FILTER ||
            UserModel.getCrewDisplayStatus(member) === statusFilter),
      ),
    [crew, roleFilter, statusFilter],
  );

  const handleFilterChange = (setFilter: (value: string) => void) => {
    return (value: string) => {
      setFilter(value);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };
  };

  const handleOpenForm = (member?: CrewMember) => {
    setSelectedMember(member);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
  };

  const handleSaveMember = (member: CrewMember) => {
    if (selectedMember) {
      onUpdateMember(member);
    } else {
      onCreateMember(member);
    }

    handleCloseForm();
  };

  const columns: GridColDef<CrewMember>[] = [
    {
      field: "name",
      headerName: "Crew Member",
      flex: 1.4,
      minWidth: 220,
      valueGetter: (_value, row) => getFullName(row),
      renderCell: ({ row }) => (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: theme.palette.primary.main,
            }}
          >
            {getInitials(row)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--dashboard-text-primary)",
              }}
            >
              {getFullName(row)}
            </Typography>
            <Typography
              noWrap
              sx={{ fontSize: 11, color: "var(--dashboard-text-secondary)" }}
            >
              {row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "roleCode",
      headerName: "Role",
      width: 150,
      valueGetter: (_value, row) => getRoleLabel(row),
      renderCell: ({ row }) => (
        <Chip
          label={getRoleLabel(row)}
          size="small"
          variant="outlined"
          color={CREW_ROLE_CHIP_COLORS[row.roleCode] ?? "default"}
          sx={{ borderRadius: "6px", fontWeight: 500 }}
        />
      ),
    },
    {
      field: "skills",
      headerName: "Skills",
      width: 210,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {row.skills.slice(0, CREW_VISIBLE_SKILLS).map((skill) => (
            <CrewSkillTag key={skill.name} label={skill.name} />
          ))}
          {row.skills.length > CREW_VISIBLE_SKILLS && (
            <CrewSkillTag
              label={`+${row.skills.length - CREW_VISIBLE_SKILLS}`}
            />
          )}
        </Box>
      ),
    },
    {
      field: "baseRate",
      headerName: "Base Rate",
      width: 110,
      renderCell: ({ row }) =>
        row.baseRate === null ? (
          <EmptyCell />
        ) : (
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
              {formatCurrency(row.baseRate)}
              <Box
                component="span"
                sx={{
                  fontWeight: 400,
                  color: "var(--dashboard-text-secondary)",
                }}
              >
                /hr
              </Box>
            </Typography>
            {row.overtimeRate !== null && (
              <Typography
                sx={{ fontSize: 11, color: theme.palette.warning.main }}
              >
                OT: {formatCurrency(row.overtimeRate)}
              </Typography>
            )}
          </Box>
        ),
    },
    {
      field: "hoursThisWeek",
      headerName: "Hrs / Wk",
      width: 110,
      renderCell: ({ row }) =>
        row.hoursThisWeek === undefined ? (
          <EmptyCell />
        ) : (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: row.overtime
                    ? theme.palette.warning.main
                    : "var(--dashboard-text-primary)",
                }}
              >
                {row.hoursThisWeek}h
              </Typography>
              {row.overtime && (
                <Box
                  component="span"
                  sx={{
                    px: 0.5,
                    borderRadius: "4px",
                    fontSize: 10,
                    fontWeight: 700,
                    color: theme.palette.warning.main,
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                  }}
                >
                  OT
                </Box>
              )}
            </Box>
            <Box
              className="dashboard-progress-track"
              sx={{ height: 4, width: 64, mt: 0.5 }}
            >
              <Box
                className="dashboard-progress-bar"
                sx={{
                  width: `${Math.min((row.hoursThisWeek / CREW_MAX_WEEKLY_HOURS) * 100, 100)}%`,
                  backgroundColor: row.overtime
                    ? theme.palette.warning.main
                    : theme.palette.primary.main,
                }}
              />
            </Box>
          </Box>
        ),
    },
    {
      field: "availability",
      headerName: "Availability",
      width: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <CrewAvailability availability={row.availability} />
      ),
    },
    {
      field: "canDrive",
      headerName: "Can Drive",
      width: 100,
      renderCell: ({ row }) =>
        CREW_ROLES_WITHOUT_CAN_DRIVE.includes(
          row.roleCode as Role,
        ) ? null : row.canDrive ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: "var(--dashboard-success)",
            }}
          >
            <CheckIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Yes</Typography>
          </Box>
        ) : (
          <Typography
            sx={{ fontSize: 13, color: "var(--dashboard-text-secondary)" }}
          >
            No
          </Typography>
        ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      valueGetter: (_value, row) => UserModel.getCrewDisplayStatus(row),
      renderCell: ({ row }) => {
        const displayStatus = UserModel.getCrewDisplayStatus(row);

        return (
          <Chip
            label={displayStatus}
            size="small"
            variant="outlined"
            color={CREW_STATUS_CHIP_COLORS[displayStatus]}
            sx={{ borderRadius: "6px", fontWeight: 500 }}
          />
        );
      },
    },
  ];

  if (hasWritePermission) {
    columns.push({
      field: "actions",
      headerName: "",
      width: 70,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title="Edit">
          <IconButton
            aria-label="edit"
            onClick={(event) => {
              event.stopPropagation();
              handleOpenForm(row);
            }}
          >
            <EditOutlinedIcon
              fontSize="small"
              sx={{ color: theme.palette.primary.main }}
            />
          </IconButton>
        </Tooltip>
      ),
    });
  }

  return (
    <Box className="dashboard-page">
      <Box className="dashboard-header">
        <Box sx={{ minWidth: 0 }}>
          <Typography className="dashboard-subtitle">
            Team members, roles, skills, pay and scheduling
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            width: { xs: "100%", md: "auto" },
          }}
        >
          {isSuperAdmin && (
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: "100%", sm: 220 },
                maxWidth: { xs: "100%", sm: 320 },
                backgroundColor: "var(--dashboard-surface)",
                borderRadius: "var(--dashboard-radius-control)",
              }}
            >
              <InputLabel>Organization</InputLabel>
              <Select
                value={organizationsDropdown.length ? selectedOrgId : ""}
                label="Organization"
                onChange={(event) => onOrgChange(Number(event.target.value))}
                sx={{
                  height: 42,
                  borderRadius: "var(--dashboard-radius-control)",
                }}
              >
                {organizationsDropdown.map((organization) => (
                  <MenuItem key={organization.value} value={organization.value}>
                    {organization.key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {hasWritePermission && (
            <Button
              label="Add User"
              variantType="primary"
              icon={AddIcon}
              onClick={() => handleOpenForm()}
              sx={{
                height: 42,
                px: 2.5,
                whiteSpace: "nowrap",
                width: { xs: "100%", sm: "auto" },
              }}
            />
          )}
        </Box>
      </Box>

      <CrewSummaryCards crew={crew} />

      <Box className="dashboard-card" sx={{ mt: 3, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            gap: 2,
            p: 2,
            borderBottom: "1px solid var(--dashboard-border-row)",
          }}
        >
          <CrewFilterChips
            label="Role"
            options={roleFilters}
            value={roleFilter}
            onChange={handleFilterChange(setRoleFilter)}
          />
          <CrewFilterChips
            label="Status"
            options={CREW_STATUS_FILTERS}
            value={statusFilter}
            onChange={handleFilterChange(setStatusFilter)}
          />
        </Box>

        <DataGrid
          columns={columns}
          rows={filteredCrew}
          loading={isLoading}
          checkboxSelection={false}
          autoHeight
          rowHeight={64}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          onRowClick={({ row }) => hasWritePermission && handleOpenForm(row)}
          sx={{
            border: "none",
            px: 2,
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-row": {
              cursor: hasWritePermission ? "pointer" : "default",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "var(--dashboard-text-secondary)",
            },
          }}
        />
      </Box>

      <Modal
        open={isFormOpen}
        onClose={handleCloseForm}
        title={selectedMember ? getFullName(selectedMember) : "Add User"}
        content={
          <CrewForm
            id="crewMemberForm"
            defaultValues={selectedMember}
            handleEdit={handleSaveMember}
          />
        }
        form="crewMemberForm"
        primaryButtonText={selectedMember ? "Save Changes" : "Add Member"}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={handleCloseForm}
        sx={{
          modal: {
            width: { xs: "90%", sm: "70%", md: "55%", lg: "40%" },
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
          content: {
            overflowY: "auto",
            flex: 1,
          },
        }}
      />
    </Box>
  );
};
